import React, { useCallback, useMemo } from 'react';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { Lightbulb, X } from 'lucide-react';

import { useCreateTicketFromSignal } from '@/api/churn-signals/useCreateTicketFromSignal';
import { useCreateTicket } from '@/api/tickets/useCreateTicket';
import { Button } from '@/component-library/primitives/Button/Button';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ChurnSignal } from '@/models/DashboardData';
import { firstChurnSignalGuid } from '@/utils/churnSignalUuid';
import { AppRoutes } from '@/utils/routes/router';

export function riskFactorDescriptions(factors: unknown): string[] {
  if (!Array.isArray(factors)) return [];
  return factors
    .map((factor) => {
      if (typeof factor === 'string') return factor;
      if (
        factor &&
        typeof factor === 'object' &&
        'description' in factor &&
        typeof (factor as { description: unknown }).description === 'string'
      ) {
        return (factor as { description: string }).description;
      }
      return '';
    })
    .filter(Boolean);
}

function memberRiskFactorLines(member: MemberOverview): string[] {
  const fromAnalysis = member.churnRiskAnalysis?.riskFactors;
  if (fromAnalysis?.length) {
    return fromAnalysis.map((f) => f.description).filter(Boolean);
  }
  return riskFactorDescriptions(member.parsedChurnRiskData?.riskFactors ?? member.churnRiskData?.riskFactors);
}

export type RecommendedActionExplanationContext =
  | { type: 'signal'; signal: ChurnSignal }
  | { type: 'member'; member: MemberOverview };

export interface RecommendedActionExplanationModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: string;
  context: RecommendedActionExplanationContext;
  /** Shown on the footer button (e.g. "Back" when stacked on another modal). */
  closeButtonLabel?: string;
  /** Called after a ticket is created successfully (e.g. close parent modals). */
  onSuccessfulTicketCreate?: () => void;
}

export const RecommendedActionExplanationModal: React.FC<RecommendedActionExplanationModalProps> = ({
  isOpen,
  onClose,
  action,
  context,
  closeButtonLabel = 'Close',
  onSuccessfulTicketCreate,
}) => {
  const navigate = useNavigate();
  const createTicketFromSignalMutation = useCreateTicketFromSignal();
  const createTicketMutation = useCreateTicket();

  const signalTypeLabel = context.type === 'signal' ? context.signal.signalType.replace(/_/g, ' ') : null;

  const modelConfidencePct = useMemo(() => {
    if (context.type !== 'signal') return null;
    const c = context.signal.metadata?.confidence;
    if (c == null) return null;
    return `${Math.round(c * 100)}%`;
  }, [context]);

  const riskFactorList = useMemo(() => {
    if (context.type === 'signal') {
      return riskFactorDescriptions(context.signal.churnRiskData?.riskFactors);
    }
    return memberRiskFactorLines(context.member);
  }, [context]);

  const whatThisAddresses = useMemo(() => {
    if (context.type === 'signal') {
      return context.signal.description;
    }
    const m = context.member;
    const churn = m.parsedChurnRiskData ?? m.churnRiskData;
    const score = m.churnRiskAnalysis?.riskScore ?? churn?.riskScore;
    const engagement = m.engagement ?? churn?.engagementLevel;
    const status = m.status ?? churn?.subscriptionStatus;
    const parts: string[] = [];
    if (score != null) parts.push(`churn risk score of ${score}`);
    if (engagement) parts.push(`${engagement} engagement`);
    if (status) parts.push(`${status} subscription status`);
    if (m.lastActivity && churn?.activityMetrics?.lastActivityDate) {
      parts.push('recent activity patterns consistent with elevated churn risk');
    } else if (m.lastActivity) {
      parts.push('activity history we track for this member');
    }
    if (parts.length === 0) {
      return "This recommendation comes from this member's churn risk profile and related signals we compute for your workspace—not from a single open signal.";
    }
    return `We surfaced this recommendation from this member's profile: ${parts.join(
      ', ',
    )}. Together, these indicators suggest proactive retention work may be valuable.`;
  }, [context]);

  const whyWeSuggest = useMemo(() => {
    if (context.type === 'signal') {
      const s = context.signal;
      return (
        <>
          This action is paired with this churn signal because it is a practical next step for the pattern we detected (
          {signalTypeLabel}, priority {s.priority}, risk score {s.riskScore}
          {modelConfidencePct ? `, model confidence ${modelConfidencePct}` : ''}). Recommended actions are chosen to
          reduce churn risk by responding to that pattern—not as a guarantee of outcome, but as guidance aligned with
          how we interpreted the member&apos;s situation.
        </>
      );
    }
    const m = context.member;
    const churn = m.parsedChurnRiskData ?? m.churnRiskData;
    const score = m.churnRiskAnalysis?.riskScore ?? churn?.riskScore ?? 'N/A';
    const engagement = m.engagement ?? churn?.engagementLevel ?? 'unknown';
    return (
      <>
        This action appears in the member&apos;s recommended actions because it matches the retention playbook we apply
        when risk looks like this (risk score {score}, engagement {engagement}). It is meant as operational guidance
        based on the model and rules behind your workspace—not a promise of outcome.
      </>
    );
  }, [context, modelConfidencePct, signalTypeLabel]);

  const churnPredictionSection = useMemo(() => {
    if (context.type === 'signal') {
      const p = context.signal.churnRiskData?.churnPrediction;
      if (p?.churnProbability == null) return null;
      return { probability: p.churnProbability, confidence: p.confidenceScore };
    }
    const p = context.member.parsedChurnRiskData?.churnPrediction ?? context.member.churnRiskData?.churnPrediction;
    if (p?.churnProbability == null) return null;
    return { probability: p.churnProbability, confidence: p.confidenceScore };
  }, [context]);

  const signalUuid = useMemo(() => {
    if (context.type !== 'signal') return null;
    return firstChurnSignalGuid(context.signal.id, context.signal.signalId);
  }, [context]);

  const memberTicketDescription = useMemo(() => {
    if (context.type !== 'member') return '';
    const lines = [
      `Recommended action: ${action}`,
      '',
      'Context:',
      whatThisAddresses,
      '',
      `Member: ${context.member.name} (${context.member.email})`,
      `Member ID: ${context.member.id}`,
    ];
    if (riskFactorList.length > 0) {
      lines.push('', 'Risk factors:', ...riskFactorList.map((r) => `• ${r}`));
    }
    return lines.join('\n');
  }, [action, context, riskFactorList, whatThisAddresses]);

  const createTicketDisabled = context.type === 'signal' ? !signalUuid : !context.member.id;

  const ticketMutationLoading = createTicketFromSignalMutation.isLoading || createTicketMutation.isLoading;

  const handleCreateTicket = useCallback(async () => {
    try {
      if (context.type === 'signal') {
        if (!signalUuid) {
          console.error('Cannot create ticket: signal has no id');
          return;
        }
        await createTicketFromSignalMutation.mutateAsync(signalUuid);
      } else {
        const title = action.length > 120 ? `${action.slice(0, 117)}...` : action;
        await createTicketMutation.mutateAsync({
          title,
          description: memberTicketDescription,
          audienceMemberIds: [context.member.id],
        });
      }
      if (onSuccessfulTicketCreate) {
        onSuccessfulTicketCreate();
      } else {
        onClose();
      }
      navigate(AppRoutes.tickets);
    } catch (error) {
      console.error('Error creating ticket from recommended action:', error);
    }
  }, [
    action,
    context,
    createTicketFromSignalMutation,
    createTicketMutation,
    memberTicketDescription,
    navigate,
    onClose,
    onSuccessfulTicketCreate,
    signalUuid,
  ]);

  if (!isOpen) return null;

  const subtitle =
    context.type === 'signal' ? (
      <>
        From signal: <span className="text-custom-black font-medium">{context.signal.title}</span>
        {context.signal.userId ? (
          <>
            {' '}
            ·{' '}
            <Link
              to={generatePath(AppRoutes.memberProfile, { id: context.signal.userId })}
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-custom-blue hover:text-custom-darkBlue hover:underline"
            >
              {context.signal.userName || context.signal.userEmail || 'Member'}
            </Link>
          </>
        ) : (
          <>
            {' '}
            ·{' '}
            <span className="text-custom-black font-medium">
              {context.signal.userName || context.signal.userEmail || 'Member'}
            </span>
          </>
        )}
      </>
    ) : (
      <>
        Based on churn profile for{' '}
        {context.member.id ? (
          <Link
            to={generatePath(AppRoutes.memberProfile, { id: context.member.id })}
            onClick={(e) => e.stopPropagation()}
            className="text-custom-black font-medium text-custom-blue hover:text-custom-darkBlue hover:underline"
          >
            {context.member.name || context.member.email}
          </Link>
        ) : (
          <span className="text-custom-black font-medium">{context.member.name || context.member.email}</span>
        )}
      </>
    );

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto border border-custom-mediumSilver"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recommended-action-detail-title"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-custom-mediumSilver">
          <div className="flex items-start gap-3 min-w-0">
            <span className="mt-0.5 shrink-0 text-custom-blue">
              <Lightbulb className="w-5 h-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2
                id="recommended-action-detail-title"
                className="text-base font-semibold text-custom-black leading-snug"
              >
                {action}
              </h2>
              <p className="text-xs text-custom-darkSilver mt-1">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-custom-darkSilver hover:text-custom-black transition-colors p-1 rounded-md hover:bg-custom-silver"
            aria-label="Close explanation"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-custom-darkSilver mb-2">
              What this addresses
            </h3>
            <p className="text-xs text-custom-black leading-relaxed">{whatThisAddresses}</p>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-custom-darkSilver mb-2">
              Why we suggest it
            </h3>
            <p className="text-xs text-custom-black leading-relaxed">{whyWeSuggest}</p>
          </section>

          {riskFactorList.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-custom-darkSilver mb-2">
                Evidence from risk analysis
              </h3>
              <ul className="space-y-2">
                {riskFactorList.map((desc, i) => (
                  <li key={i} className="text-xs text-custom-darkSilver flex gap-2">
                    <span className="text-custom-blue shrink-0">•</span>
                    <span>{desc}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {churnPredictionSection && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-custom-darkSilver mb-2">
                Model output
              </h3>
              <p className="text-xs text-custom-black">
                Estimated churn probability:{' '}
                <span className="font-semibold">{(churnPredictionSection.probability * 100).toFixed(1)}%</span>
                {churnPredictionSection.confidence != null && (
                  <> (confidence {(churnPredictionSection.confidence * 100).toFixed(1)}%)</>
                )}
              </p>
            </section>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-custom-mediumSilver bg-custom-silver rounded-b-lg">
          <Button variant="transparent" onClick={onClose}>
            {closeButtonLabel}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTicket}
            disabled={createTicketDisabled || ticketMutationLoading}
          >
            {ticketMutationLoading ? 'Creating...' : 'Create ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
};
